import React, { useState, useEffect, useMemo } from 'react';
import { approvalSLAsAPI, departmentsAPI } from '../api/client';
import { Plus, Edit2, Trash2, Star, Clock, AlertTriangle, Shield, X, Search } from 'lucide-react';
import SortableTh from '../components/SortableTh';
import TablePagination from '../components/TablePagination';

const SLA_PAGE_SIZE = 25;

export default function ApprovalSLAManagement() {
  const [slas, setSlas] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [slaSearch, setSlaSearch] = useState('');
  const [slasPage, setSlasPage] = useState(1);

  const emptyForm = {
    SLAName: '',
    Description: '',
    TargetHours: 48,
    WarningHours: 24,
    AppliesTo: 'All',
    DepartmentID: '',
    MinAmount: '',
    MaxAmount: '',
    Priority: 1,
    IsDefault: false
  };
  const [formData, setFormData] = useState(emptyForm);

  const appliesToOptions = [
    { value: 'All', label: 'All Invoices' },
    { value: 'Department', label: 'Specific Department' },
    { value: 'AmountRange', label: 'Amount Range' },
    { value: 'DepartmentAndAmount', label: 'Department + Amount' }
  ];

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 4000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 6000); return () => clearTimeout(t); } }, [error]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [slasRes, deptRes] = await Promise.all([
        approvalSLAsAPI.getAll(),
        departmentsAPI.getAll()
      ]);
      setSlas(slasRes.data?.slas || []);
      const depts = deptRes.data?.departments || deptRes.data || [];
      setDepartments(depts.filter(d => d.IsActive !== false));
    } catch (err) {
      console.error('Fetch SLAs error:', err);
      setError('Failed to load SLA configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleEdit = (sla) => {
    setEditing(sla);
    setFormData({
      SLAName: sla.SLAName,
      Description: sla.Description || '',
      TargetHours: sla.TargetHours,
      WarningHours: sla.WarningHours || '',
      AppliesTo: sla.AppliesTo,
      DepartmentID: sla.DepartmentID || '',
      MinAmount: sla.MinAmount || '',
      MaxAmount: sla.MaxAmount || '',
      Priority: sla.Priority,
      IsDefault: sla.IsDefault
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        TargetHours: parseInt(formData.TargetHours),
        WarningHours: formData.WarningHours ? parseInt(formData.WarningHours) : null,
        Priority: parseInt(formData.Priority),
        DepartmentID: formData.DepartmentID || null,
        MinAmount: formData.MinAmount ? parseFloat(formData.MinAmount) : null,
        MaxAmount: formData.MaxAmount ? parseFloat(formData.MaxAmount) : null,
      };

      if (editing) {
        await approvalSLAsAPI.update(editing.SLAID, payload);
        setSuccess('SLA updated successfully');
      } else {
        await approvalSLAsAPI.create(payload);
        setSuccess('SLA created successfully');
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Save SLA error:', err);
      setError(err.response?.data?.error || 'Failed to save SLA');
    }
  };

  const handleDelete = async (sla) => {
    if (sla.IsDefault) {
      setError('Cannot delete the default SLA. Set another SLA as default first.');
      return;
    }
    setDeleting(sla.SLAID);
    try {
      await approvalSLAsAPI.delete(sla.SLAID);
      setSuccess('SLA deactivated successfully');
      fetchData();
    } catch (err) {
      setError('Failed to delete SLA');
    } finally {
      setDeleting(null);
    }
  };

  const showDepartment = formData.AppliesTo === 'Department' || formData.AppliesTo === 'DepartmentAndAmount';
  const showAmount = formData.AppliesTo === 'AmountRange' || formData.AppliesTo === 'DepartmentAndAmount';

  const formatHours = (hours) => {
    if (!hours && hours !== 0) return '-';
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    const remainder = hours % 24;
    return remainder ? `${days}d ${remainder}h` : `${days}d`;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const activeSlas = slas.filter(s => s.IsActive);
  const inactiveSlas = slas.filter(s => !s.IsActive);

  const sortedActiveSlas = [...activeSlas].sort((a, b) => {
    if (!sortField) return 0;
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (sortField === 'SLAName' || sortField === 'AppliesTo') {
      aVal = (aVal || '').toString();
      bVal = (bVal || '').toString();
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (sortField === 'IsDefault') {
      aVal = aVal ? 1 : 0;
      bVal = bVal ? 1 : 0;
    }
    aVal = aVal ?? 0;
    bVal = bVal ?? 0;
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const filteredActiveSlas = useMemo(() => {
    if (!slaSearch) return sortedActiveSlas;
    const q = slaSearch.toLowerCase();
    return sortedActiveSlas.filter(s =>
      (s.SLAName || '').toLowerCase().includes(q) ||
      (s.Description || '').toLowerCase().includes(q) ||
      (s.AppliesTo || '').toLowerCase().includes(q)
    );
  }, [sortedActiveSlas, slaSearch]);
  const pagedActiveSlas = filteredActiveSlas.slice((slasPage - 1) * SLA_PAGE_SIZE, slasPage * SLA_PAGE_SIZE);
  React.useEffect(() => { setSlasPage(1); }, [slaSearch, sortField, sortDir]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Approval SLA Management</h1>
          <p className="text-sm text-gray-600 mt-1">
            Define Service Level Agreements for invoice approval turnaround times. Approvers are measured against these targets.
          </p>
        </div>
        <button onClick={handleAdd} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Add SLA
        </button>
      </div>

      {/* Alerts */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 text-green-800 text-sm">
          <Shield className="w-4 h-4" /> {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-800 text-sm">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* No SLAs hint */}
      {activeSlas.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <Clock className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No SLAs configured</h3>
          <p className="text-sm text-gray-600 mb-4">
            Create your first SLA to start tracking approval performance against targets.
            SLA compliance will appear in the Approver Performance dashboard.
          </p>
          <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
            Create First SLA
          </button>
        </div>
      )}

      {/* Active SLAs Table */}
      {activeSlas.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-700 flex-1">Active SLAs ({filteredActiveSlas.length}{slaSearch ? ` of ${activeSlas.length}` : ''})</h2>
            <div className="relative w-52">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search SLAs..."
                value={slaSearch}
                onChange={e => setSlaSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh field="SLAName" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Name</SortableTh>
                  <SortableTh field="TargetHours" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Target</SortableTh>
                  <SortableTh field="WarningHours" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Warning</SortableTh>
                  <SortableTh field="AppliesTo" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Applies To</SortableTh>
                  <SortableTh field="Priority" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Priority</SortableTh>
                  <SortableTh field="IsDefault" sortBy={sortField} sortDir={sortDir} onSort={handleSort}>Default</SortableTh>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedActiveSlas.map(sla => (
                  <tr key={sla.SLAID} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{sla.SLAName}</div>
                      {sla.Description && <div className="text-xs text-gray-500 mt-0.5">{sla.Description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-700">
                        <Clock className="w-3.5 h-3.5" /> {formatHours(sla.TargetHours)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-yellow-600">{formatHours(sla.WarningHours)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-700">
                        {sla.AppliesTo === 'All' && 'All Invoices'}
                        {sla.AppliesTo === 'Department' && (
                          <span>Dept: <span className="font-medium">{sla.DepartmentName || 'N/A'}</span></span>
                        )}
                        {sla.AppliesTo === 'AmountRange' && (
                          <span>£{sla.MinAmount?.toLocaleString() || '0'} – £{sla.MaxAmount?.toLocaleString() || '∞'}</span>
                        )}
                        {sla.AppliesTo === 'DepartmentAndAmount' && (
                          <span>{sla.DepartmentName || 'N/A'} / £{sla.MinAmount?.toLocaleString() || '0'}–£{sla.MaxAmount?.toLocaleString() || '∞'}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{sla.Priority}</td>
                    <td className="px-4 py-3">
                      {sla.IsDefault ? (
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleEdit(sla)} className="text-blue-600 hover:text-blue-800 p-1" title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(sla)}
                          disabled={deleting === sla.SLAID}
                          className="text-red-500 hover:text-red-700 p-1 disabled:opacity-50"
                          title="Deactivate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination page={slasPage} pageSize={SLA_PAGE_SIZE} total={filteredActiveSlas.length} onPageChange={setSlasPage} />
        </div>
      )}

      {/* Inactive SLAs (collapsed) */}
      {inactiveSlas.length > 0 && (
        <details className="bg-white rounded-lg border border-gray-200">
          <summary className="px-4 py-3 cursor-pointer text-sm font-semibold text-gray-500 hover:text-gray-700">
            Inactive SLAs ({inactiveSlas.length})
          </summary>
          <div className="overflow-x-auto border-t">
            <table className="min-w-full divide-y divide-gray-200">
              <tbody className="bg-gray-50 divide-y divide-gray-200">
                {inactiveSlas.map(sla => (
                  <tr key={sla.SLAID} className="opacity-60">
                    <td className="px-4 py-2 text-sm text-gray-500">{sla.SLAName}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{formatHours(sla.TargetHours)}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{sla.AppliesTo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* SLA Info Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">How SLAs Work</h3>
        <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
          <li><strong>Target Hours:</strong> The maximum time an approver should take to approve/reject an invoice after it is assigned to them.</li>
          <li><strong>Warning Hours:</strong> A warning threshold &mdash; after this time, the invoice is considered at-risk of breaching the SLA.</li>
          <li><strong>Priority:</strong> When multiple SLAs could apply, the one with the lowest priority number is used.</li>
          <li><strong>Default SLA:</strong> Applied to all invoices that don't match a more specific rule.</li>
          <li><strong>Compliance Reporting:</strong> SLA performance is tracked in the <em>Approver Performance</em> tab of the Finance Dashboard.</li>
          <li><strong>Coming Soon:</strong> Weekly SLA badge emails to top performers, CC'd to Department Managers and Finance Admins.</li>
        </ul>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">{editing ? 'Edit SLA' : 'Create SLA'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SLA Name *</label>
                <input
                  type="text" required maxLength={255}
                  value={formData.SLAName} onChange={e => setFormData(f => ({ ...f, SLAName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Standard 48h, Urgent 24h, High Value 72h"
                />
              </div>
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text" maxLength={500}
                  value={formData.Description} onChange={e => setFormData(f => ({ ...f, Description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of when this SLA applies"
                />
              </div>
              {/* Target + Warning */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Hours *</label>
                  <input
                    type="number" required min={1} max={9999}
                    value={formData.TargetHours} onChange={e => setFormData(f => ({ ...f, TargetHours: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formatHours(parseInt(formData.TargetHours) || 0)} approval window</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Warning Hours</label>
                  <input
                    type="number" min={1} max={9999}
                    value={formData.WarningHours} onChange={e => setFormData(f => ({ ...f, WarningHours: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Alert before breach</p>
                </div>
              </div>
              {/* Applies To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Applies To</label>
                <select
                  value={formData.AppliesTo} onChange={e => setFormData(f => ({ ...f, AppliesTo: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  {appliesToOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {/* Department (conditional) */}
              {showDepartment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select
                    value={formData.DepartmentID} onChange={e => setFormData(f => ({ ...f, DepartmentID: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Department</option>
                    {departments.map(d => <option key={d.DepartmentID} value={d.DepartmentID}>{d.Name}</option>)}
                  </select>
                </div>
              )}
              {/* Amount (conditional) */}
              {showAmount && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Amount</label>
                    <input
                      type="number" step="0.01" min={0}
                      value={formData.MinAmount} onChange={e => setFormData(f => ({ ...f, MinAmount: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Amount</label>
                    <input
                      type="number" step="0.01" min={0}
                      value={formData.MaxAmount} onChange={e => setFormData(f => ({ ...f, MaxAmount: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="No limit"
                    />
                  </div>
                </div>
              )}
              {/* Priority + Default */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number" min={1} max={99}
                    value={formData.Priority} onChange={e => setFormData(f => ({ ...f, Priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower = higher priority</p>
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.IsDefault}
                      onChange={e => setFormData(f => ({ ...f, IsDefault: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Set as Default SLA</span>
                  </label>
                </div>
              </div>
              {/* Submit */}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                  {editing ? 'Update SLA' : 'Create SLA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
